import PaginationData from 'types/models/PaginationData';
import {
  StockMovement,
  StockMovementDestinationType,
  StockMovementItem,
  StockMovementLocation,
  StockMovementPayload,
  StockMovementStatus,
  WarehouseProductStock,
} from 'types/models/StockMovement';
import axiosInstance from './axios';
import joinURLParams from 'utils/joinUrlParams';
import { parseUser } from './authServices';

export const getStockMovements = async (
  page?: number,
  searchTerm?: string,
  extraParams?: Record<string, any>,
): Promise<PaginationData<StockMovement>> => {
  let url = joinURLParams({ page, searchTerm }, 'stock/movements');

  if (extraParams) {
    url = joinURLParams(extraParams, url);
  }

  const { data } = await axiosInstance({
    method: 'get',
    url,
  });

  return {
    currentPage: data.current_page,
    data: parseMovements(data.data),
    perPage: data.per_page,
    total: data.total,
  };
};

export const getStockMovement = async (id: string): Promise<StockMovement> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `stock/movements/${id}`,
    });
    return parseMovement(data);
  } catch (error) {
    throw error;
  }
};

export const storeStockMovement = async (payload: StockMovementPayload): Promise<{ result: boolean }> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: 'stock/movements',
      data: payload,
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateStockMovement = async (id: string, payload: StockMovementPayload): Promise<{ result: boolean }> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `stock/movements/${id}`,
      data: payload,
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const getWarehouseStocks = async ({
  warehouseId,
}: {
  warehouseId: string;
}): Promise<WarehouseProductStock[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `warehouses/${warehouseId}/stocks`,
    });
    return parseWarehouseProductStocks(data);
  } catch (error) {
    throw error;
  }
};

export const destroyStockMovement = async (id: string): Promise<void> => {
  const { data } = await axiosInstance({
    method: 'delete',
    url: `stock/movements/${id}`,
  });
  return data;
};

export const storeStockTransfer = async (payload: any): Promise<StockMovement> => {
  const { data } = await axiosInstance({
    method: 'post',
    url: 'stock/movements/transfer',
    data: payload,
  });
  return parseMovement(data);
};

export const storeStockAdjustment = async (payload: any): Promise<StockMovement> => {
  const { data } = await axiosInstance({
    method: 'post',
    url: 'stock/movements/adjustment',
    data: payload,
  });
  return parseMovement(data);
};

export const storeStockLoss = async (payload: any): Promise<StockMovement> => {
  const { data } = await axiosInstance({
    method: 'post',
    url: 'stock/movements/loss',
    data: payload,
  });
  return parseMovement(data);
};

export const getPendingCommerceMovements = async (
  commerceId: string,
  page?: number,
  searchTerm?: string,
): Promise<PaginationData<StockMovement>> => {
  const url = joinURLParams(
    {
      page,
      searchTerm,
      status: StockMovementStatus.DRAFT,
      destinationType: StockMovementDestinationType.COMMERCE,
      destinationId: commerceId,
    },
    'stock/movements',
  );

  const { data } = await axiosInstance({
    method: 'get',
    url,
  });

  return {
    currentPage: data.current_page,
    data: parseMovements(data.data),
    perPage: data.per_page,
    total: data.total,
  };
};

export const getPendingWarehouseMovements = async (
  warehouseId: string,
  page?: number,
  searchTerm?: string,
): Promise<PaginationData<StockMovement>> => {
  const url = joinURLParams(
    {
      page,
      searchTerm,
      status: StockMovementStatus.DRAFT,
      destinationType: StockMovementDestinationType.WAREHOUSE,
      destinationId: warehouseId,
    },
    'stock/movements',
  );

  const { data } = await axiosInstance({
    method: 'get',
    url,
  });

  return {
    currentPage: data.current_page,
    data: parseMovements(data.data),
    perPage: data.per_page,
    total: data.total,
  };
};

export const acceptStockMovement = async (movementId: string, receivedByUserId: string): Promise<StockMovement> => {
  const { data } = await axiosInstance({
    method: 'post',
    url: `stock/movements/${movementId}/accept`,
    data: { receivedByUserId },
  });
  return parseMovement(data);
};

export const cancelStockMovement = async (movementId: string, cancelledByUserId?: string): Promise<StockMovement> => {
  const { data } = await axiosInstance({
    method: 'post',
    url: `stock/movements/${movementId}/cancel`,
    data: { cancelledByUserId },
  });
  return parseMovement(data);
};

const parseWarehouseProductStocks = (warehouseStocks: any[]): WarehouseProductStock[] => {
  return warehouseStocks.map((warehouseStock) => {
    return {
      id: warehouseStock.id,
      name: warehouseStock.name,
      productId: warehouseStock.product_id,
      sku: warehouseStock.sku,
      stock: warehouseStock.current_stock,
    };
  });
};

const parseMovements = (movements: unknown[]): StockMovement[] => {
  return movements.map(parseMovement);
};

const parseMovement = (movement: any): StockMovement => {
  return {
    id: movement.id,
    venueId: movement.venue_id,
    createdByUserId: movement.created_by_user_id,
    orderId: movement.order_id,
    stockReceptionId: movement.stock_reception_id,
    type: movement.type,

    sourceType: movement.source_type,
    sourceId: movement.source_id,

    destinationType: movement.destination_type,
    destinationId: movement.destination_id,

    reason: movement.reason,
    status: movement.status,
    createdAt: movement.created_at,
    localDatetime: movement.local_datetime ?? null,
    recipeId: movement.recipe_id ?? null,
    recipe: movement.recipe
      ? {
          id: movement.recipe.id,
          globalProductId: movement.recipe.global_product_id,
          productName: movement.recipe.product_name ?? null,
        }
      : null,
    source: movement.source ? parseMovementLocation(movement.source) : undefined,
    destination: movement.destination ? parseMovementLocation(movement.destination) : undefined,

    createdBy: movement.created_by ? parseUser(movement.created_by) : undefined,
    receivedByUserId: movement.received_by_user_id ?? null,
    receivedBy: movement.received_by ? parseUser(movement.received_by) : undefined,
    items: movement.items ? parseMovementItems(movement.items) : undefined,
  };
};

const parseMovementItems = (items: unknown[]): StockMovementItem[] => {
  return items.map(parseMovementItem);
};

const parseMovementItem = (item: any): StockMovementItem => {
  return {
    id: item.id,
    quantity: item.quantity,
    unitCost: item.unit_cost,
    globalProductId: item.global_product_id,
    stockMovementId: item.stock_movement_id,
    operation: item.operation ?? null,
    warehouseId: item.warehouse_id ?? null,
    averageCostSnapshot: item.average_cost_snapshot ?? null,
    replacementCostSnapshot: item.replacement_cost_snapshot ?? null,
    globalProduct: item.global_product
      ? {
          id: item.global_product.id,
          name: item.global_product.name,
          sku: item.global_product.sku,
          unitMeasure: item.global_product.unit_measure ?? null,
          receptionUnitMeasure: item.global_product.reception_unit_measure ?? null,
          unitsPerReceptionUnit:
            item.global_product.units_per_reception_unit !== null &&
            item.global_product.units_per_reception_unit !== undefined
              ? Number(item.global_product.units_per_reception_unit)
              : null,
        }
      : null,
    createdAt: item.created_at,
  };
};

const parseMovementLocation = (location: any): StockMovementLocation => {
  return {
    id: location.id,
    name: location.name,
    type: location.type,
  };
};
